import { useCallback, useEffect, useMemo, useState } from 'react'
import alertService from '@/services/alerts/alert.service'
import cmsApi from '../cms.api'
import { CmsEntity, CmsFormState, CmsFormValue, CmsLookupContext, CmsResourceConfig } from '../cms.types'
import { buildPayload, createEmptyFormState, createFormStateFromItem, filterItems, validateForm } from '../cms.utils'

export interface CmsEditorState<T extends CmsEntity & Record<string, any>> {
  mode: 'create' | 'edit'
  item: T | null
  form: CmsFormState
  errors: Record<string, string>
}

const useCmsResource = <T extends CmsEntity & Record<string, any>>(
  resource: CmsResourceConfig<T>,
  context: CmsLookupContext,
  onMutated?: () => void,
  listParams?: Record<string, string | number | boolean | null | undefined>
) => {
  const [items, setItems] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [search, setSearch] = useState('')
  const [editor, setEditor] = useState<CmsEditorState<T> | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    const response = await cmsApi.list<T>(resource.endpoint, listParams)
    setLoading(false)

    if (response.status === 'success') {
      setItems(response.body)
      return
    }

    alertService.requestAlert({ type: 'error', title: 'Не удалось загрузить данные', description: response.message })
  }, [resource.endpoint, listParams])

  useEffect(() => {
    load()
  }, [load])

  const filteredItems = useMemo(() => filterItems(items, resource, search, context), [items, resource, search, context])

  const openCreate = () => {
    setEditor({
      mode: 'create',
      item: null,
      form: createEmptyFormState(resource, context),
      errors: {},
    })
  }

  const openEdit = (item: T) => {
    setEditor({
      mode: 'edit',
      item,
      form: createFormStateFromItem(resource, item),
      errors: {},
    })
  }

  const closeEditor = () => setEditor(null)

  const changeField = (value: CmsFormValue, name: string) => {
    setEditor((prev) =>
      prev
        ? {
            ...prev,
            form: { ...prev.form, [name]: value },
            errors: { ...prev.errors, [name]: '' },
          }
        : prev
    )
  }

  const save = async () => {
    if (!editor) return false

    const errors = validateForm(resource, editor.form)
    if (Object.values(errors).some(Boolean)) {
      setEditor((prev) => (prev ? { ...prev, errors } : prev))
      return false
    }

    const payload = buildPayload(resource, editor.form)
    setSaving(true)

    const response =
      editor.mode === 'create'
        ? await cmsApi.create<T>(resource.endpoint, payload)
        : await cmsApi.update<T>(
            resource.endpoint,
            resource.getEntityId ? resource.getEntityId(editor.item) : editor.item[resource.idField],
            payload
          )

    setSaving(false)

    if (response.status === 'error') {
      alertService.requestAlert({ type: 'error', title: 'Не удалось сохранить', description: response.message })
      return false
    }

    alertService.requestAlert({ type: 'success', title: 'Сохранено', description: resource.title })
    closeEditor()
    await load()
    onMutated?.()
    return true
  }

  const remove = async (item: T) => {
    const response = await cmsApi.remove<T>(
      resource.endpoint,
      resource.getEntityId ? resource.getEntityId(item) : item[resource.idField]
    )

    if (response.status === 'error') {
      alertService.requestAlert({ type: 'error', title: 'Не удалось удалить', description: response.message })
      return false
    }

    alertService.requestAlert({ type: 'success', title: 'Удалено', description: resource.title })
    await load()
    onMutated?.()
    return true
  }

  return {
    items,
    filteredItems,
    loading,
    saving,
    search,
    editor,
    setSearch,
    openCreate,
    openEdit,
    closeEditor,
    changeField,
    save,
    remove,
    reload: load,
  }
}

export default useCmsResource
